import React, { useState, useEffect } from 'react';
import tiktokAPI from '../services/tiktokAPI';

export default function LiveComments({ username, isVisible }) {
  const [comments, setComments] = useState({
    comments: [],
    totalComments: 0,
    lastUpdated: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataSource, setDataSource] = useState('real');
  const [autoScroll, setAutoScroll] = useState(true);

  // جلب التعليقات المباشرة
  const fetchLiveComments = async () => {
    if (!username || !isVisible) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await tiktokAPI.getLiveComments(username);
      
      if (result.success) {
        setComments(result.data);
        setDataSource('real');
        console.log('تم جلب التعليقات بنجاح:', result.data);
      } else {
        throw new Error(result.error || 'فشل في جلب التعليقات');
      }

    } catch (error) {
      console.error('خطأ في جلب التعليقات:', error);
      setError(`خطأ في جلب التعليقات: ${error.message}`);
      setDataSource('mock');
      
      // استخدام البيانات المحاكاة
      const mockResult = await tiktokAPI.getMockComments();
      if (mockResult.success) {
        setComments(mockResult.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // تحديث التعليقات كل 30 ثانية
  useEffect(() => {
    if (!username || !isVisible) return;

    fetchLiveComments();

    const interval = setInterval(() => {
      fetchLiveComments();
    }, 30000); // كل 30 ثانية

    return () => clearInterval(interval);
  }, [username, isVisible]);

  // التمرير التلقائي
  useEffect(() => {
    if (autoScroll && comments.comments.length > 0) {
      const commentsContainer = document.getElementById('comments-container');
      if (commentsContainer) {
        commentsContainer.scrollTop = commentsContainer.scrollHeight;
      }
    }
  }, [comments.comments, autoScroll]);

  const formatTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 60000) return 'الآن';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} دقيقة`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ساعة`;
    return `${Math.floor(diff / 86400000)} يوم`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getDataSourceColor = () => {
    return dataSource === 'real' ? '#28a745' : '#ffc107';
  };

  const getDataSourceText = () => {
    return dataSource === 'real' ? 'بيانات حقيقية' : 'بيانات محاكاة';
  };

  if (!isVisible) return null;

  return (
    <div style={{
      background: 'var(--bg-card)',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 4px 20px var(--shadow-color)',
      marginBottom: '20px',
      fontFamily: 'Cairo',
      border: '1px solid var(--border-color)'
    }}>
      <h3 style={{
        margin: '0 0 20px 0',
        color: 'var(--text-primary)',
        fontSize: 'clamp(14px, 3vw, 18px)',
        fontWeight: 'bold',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px'
      }}>
        <span>💬</span>
        التعليقات المباشرة
        <span>💬</span>
      </h3>

      {/* حالة الاتصال ومصدر البيانات */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px',
        marginBottom: '15px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isLoading ? '#ffc107' : '#28a745'
          }}></div>
          <span style={{ color: 'var(--text-secondary)' }}>
            {isLoading ? 'جاري التحميل...' : 'متصل'}
          </span>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getDataSourceColor()
            }}></div>
            <span style={{ color: 'var(--text-secondary)' }}>
              {getDataSourceText()}
            </span>
          </div>
          
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            style={{
              background: autoScroll ? 'var(--primary-color)' : 'var(--bg-disabled)',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              padding: '4px 8px',
              fontSize: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {autoScroll ? 'إيقاف التمرير' : 'تفعيل التمرير'}
          </button>
        </div>
      </div>

      {/* رسالة الخطأ */}
      {error && (
        <div style={{
          background: 'var(--error-bg)',
          color: 'var(--error-text)',
          padding: '10px 15px',
          borderRadius: '8px',
          marginBottom: '15px',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* ملخص التعليقات */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '10px',
        padding: '15px',
        marginBottom: '20px',
        border: '1px solid var(--border-color)'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '15px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{
              color: 'var(--text-primary)',
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '5px'
            }}>
              {formatNumber(comments.totalComments)}
            </div>
            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '12px'
            }}>
              إجمالي التعليقات
            </div>
          </div>
          
          <div>
            <div style={{
              color: 'var(--text-primary)',
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '5px'
            }}>
              {comments.comments.length}
            </div>
            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '12px'
            }}>
              التعليقات الحالية
            </div>
          </div>
          
          <div>
            <div style={{
              color: 'var(--text-primary)',
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '5px'
            }}>
              {comments.comments.filter(c => c.likes > 0).length}
            </div>
            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '12px'
            }}>
              تعليقات محبوبة
            </div>
          </div>
        </div>
      </div>

      {/* قائمة التعليقات */}
      <div 
        id="comments-container"
        style={{
          maxHeight: '500px',
          overflowY: 'auto',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          scrollBehavior: 'smooth'
        }}
      >
        {comments.comments.length > 0 ? (
          comments.comments.map((comment, index) => (
            <div key={comment.id} style={{
              padding: '15px',
              borderBottom: index < comments.comments.length - 1 ? '1px solid var(--border-color)' : 'none',
              background: index % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-card)',
              transition: 'background-color 0.3s ease'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                {/* صورة المرسل */}
                <div style={{
                  width: '35px',
                  height: '35px',
                  borderRadius: '50%',
                  background: 'var(--primary-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  {comment.sender.charAt(0).toUpperCase()}
                </div>
                
                {/* محتوى التعليق */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      color: 'var(--text-primary)',
                      fontWeight: 'bold',
                      fontSize: '13px'
                    }}>
                      {comment.sender}
                    </span>
                    {comment.isVerified && (
                      <span style={{ color: '#1da1f2', fontSize: '14px' }}>✓</span>
                    )}
                    <span style={{
                      color: 'var(--text-secondary)',
                      fontSize: '11px'
                    }}>
                      {formatTime(comment.timestamp)}
                    </span>
                  </div>
                  
                  <div style={{
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    marginBottom: '8px',
                    wordBreak: 'break-word'
                  }}>
                    {comment.text}
                  </div>
                  
                  {comment.likes > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)'
                    }}>
                      <span>❤️</span>
                      <span>{comment.likes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: '14px'
          }}>
            {isLoading ? 'جاري تحميل التعليقات...' : 'لا توجد تعليقات حتى الآن'}
          </div>
        )}
      </div>

      {/* آخر تحديث */}
      {comments.lastUpdated && (
        <div style={{
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--text-secondary)',
          marginTop: '15px'
        }}>
          آخر تحديث: {new Date(comments.lastUpdated).toLocaleString('ar-SA')}
        </div>
      )}
    </div>
  );
} 